<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useRouter } from 'vue-router';
import BillingAPI from 'dashboard/api/billing';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import PlanCard from './components/PlanCard.vue';
import BillingCycleToggle from './components/BillingCycleToggle.vue';
import Button from 'next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const { t } = useI18n();
const router = useRouter();
const showAlert = useAlert;

// State
const isLoading = ref(true);
const isProcessing = ref(false);
const plans = ref([]);
const currentSubscription = ref(null);
const currentPlan = ref(null);
const billingCycle = ref('monthly');
const selectedPlan = ref(null);
const confirmAction = ref(null); // 'upgrade', 'downgrade', 'subscribe'
const confirmDialogRef = ref(null);
const selectedGateway = ref('stripe'); // 'stripe' or 'asaas'

// Computed
const maxDiscount = computed(() => {
  return Math.max(...plans.value.map(p => p.yearly_discount_percentage || 0));
});

const currentPlanId = computed(() => {
  return currentPlan.value?.id;
});

const sortedPlans = computed(() => {
  return [...plans.value].sort((a, b) => a.display_order - b.display_order);
});

// Methods
const fetchData = async () => {
  isLoading.value = true;
  try {
    const [plansResponse, subscriptionResponse] = await Promise.all([
      BillingAPI.getPlans(),
      BillingAPI.getCurrentSubscription(),
    ]);

    plans.value = plansResponse.data.plans || [];
    currentSubscription.value = subscriptionResponse.data.subscription;
    currentPlan.value = subscriptionResponse.data.plan;

    // Set billing cycle from current subscription
    if (currentSubscription.value?.billing_cycle) {
      billingCycle.value = currentSubscription.value.billing_cycle;
    }
  } catch (error) {
    showAlert(t('BILLING.ERRORS.FETCH_FAILED'));
  } finally {
    isLoading.value = false;
  }
};

const isCurrentPlan = plan => {
  return plan.id === currentPlanId.value;
};

const canUpgradeTo = plan => {
  // Only show upgrade if user has an active subscription
  if (!currentSubscription.value) return false;
  if (!currentPlan.value) return false;
  return plan.monthly_price > currentPlan.value.monthly_price;
};

const canDowngradeTo = plan => {
  if (!currentPlan.value) return false;
  return plan.monthly_price < currentPlan.value.monthly_price;
};

const openConfirmDialog = (plan, action) => {
  selectedPlan.value = plan;
  confirmAction.value = action;
  confirmDialogRef.value?.open();
};

const handleSelectPlan = plan => {
  openConfirmDialog(plan, 'subscribe');
};

const handleUpgrade = plan => {
  openConfirmDialog(plan, 'upgrade');
};

const handleDowngrade = plan => {
  openConfirmDialog(plan, 'downgrade');
};

const closeConfirmDialog = () => {
  confirmDialogRef.value?.close();
  selectedPlan.value = null;
  confirmAction.value = null;
};

const confirmDialogTitle = computed(() => {
  if (!selectedPlan.value) return '';
  const titles = {
    subscribe: t('BILLING.CONFIRM.SUBSCRIBE_TITLE', {
      plan: selectedPlan.value.name,
    }),
    upgrade: t('BILLING.CONFIRM.UPGRADE_TITLE', {
      plan: selectedPlan.value.name,
    }),
    downgrade: t('BILLING.CONFIRM.DOWNGRADE_TITLE', {
      plan: selectedPlan.value.name,
    }),
  };
  return titles[confirmAction.value] || '';
});

const confirmDialogMessage = computed(() => {
  if (!selectedPlan.value) return '';
  const price =
    billingCycle.value === 'yearly'
      ? selectedPlan.value.yearly_price
      : selectedPlan.value.monthly_price;
  const cycle =
    billingCycle.value === 'yearly'
      ? t('BILLING.CYCLE.YEARLY')
      : t('BILLING.CYCLE.MONTHLY');

  const messages = {
    subscribe: t('BILLING.CONFIRM.SUBSCRIBE_MESSAGE', {
      plan: selectedPlan.value.name,
      price: `R$ ${price.toFixed(2).replace('.', ',')}`,
      cycle,
    }),
    upgrade: t('BILLING.CONFIRM.UPGRADE_MESSAGE', {
      plan: selectedPlan.value.name,
    }),
    downgrade: t('BILLING.CONFIRM.DOWNGRADE_MESSAGE', {
      plan: selectedPlan.value.name,
    }),
  };
  return messages[confirmAction.value] || '';
});

const processAction = async () => {
  if (!selectedPlan.value) return;

  isProcessing.value = true;
  try {
    let response;

    if (confirmAction.value === 'subscribe') {
      if (selectedGateway.value === 'stripe') {
        // Stripe Checkout: redirect to hosted payment page
        response = await BillingAPI.createCheckout(
          selectedPlan.value.id,
          billingCycle.value
        );
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
          return; // Page will redirect
        }
      } else {
        // Asaas flow (existing)
        response = await BillingAPI.subscribe(
          selectedPlan.value.id,
          billingCycle.value
        );
        if (response.data.payment_url) {
          window.open(response.data.payment_url, '_blank');
        }
      }
      showAlert(t('BILLING.SUCCESS.SUBSCRIBED'));
    } else if (confirmAction.value === 'upgrade') {
      response = await BillingAPI.upgrade(selectedPlan.value.id);
      showAlert(t('BILLING.SUCCESS.UPGRADED'));
    } else if (confirmAction.value === 'downgrade') {
      response = await BillingAPI.downgrade(selectedPlan.value.id);
      showAlert(t('BILLING.SUCCESS.DOWNGRADED'));
    }

    // Refresh data
    await fetchData();
  } catch (error) {
    const message =
      error.response?.data?.error || t('BILLING.ERRORS.ACTION_FAILED');
    showAlert(message);
  } finally {
    isProcessing.value = false;
    closeConfirmDialog();
  }
};

const goBack = () => {
  router.push({ name: 'billing_settings_index' });
};

// Handle Stripe redirect results from URL query params
const handleStripeRedirect = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const stripeResult = urlParams.get('stripe');
  if (stripeResult === 'success') {
    showAlert(t('BILLING.GATEWAY.STRIPE_SUCCESS'));
    // Clean URL params
    window.history.replaceState({}, '', window.location.pathname);
  } else if (stripeResult === 'cancelled') {
    showAlert(t('BILLING.GATEWAY.STRIPE_CANCELLED'));
    window.history.replaceState({}, '', window.location.pathname);
  }
};

onMounted(() => {
  handleStripeRedirect();
  fetchData();
});
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <div class="flex items-center gap-4">
        <Button icon="i-lucide-arrow-left" sm faded slate @click="goBack" />
        <BaseSettingsHeader
          :title="t('BILLING.PRICING.TITLE')"
          :description="t('BILLING.PRICING.DESCRIPTION')"
          feature-name="billing"
        />
      </div>
    </template>

    <template #body>
      <div class="max-w-6xl mx-auto">
        <!-- Billing Cycle Toggle -->
        <div class="flex justify-center mb-8">
          <BillingCycleToggle
            v-model="billingCycle"
            :discount-percentage="maxDiscount"
          />
        </div>

        <!-- Current Plan Info -->
        <div
          v-if="currentPlan"
          class="mb-8 p-4 bg-n-blue-2 border border-n-blue-6 rounded-lg"
        >
          <div class="flex items-center justify-between">
            <div>
              <span class="text-sm text-n-slate-11">
                {{ t('BILLING.PRICING.CURRENT_PLAN_INFO') }}
              </span>
              <span class="ml-2 font-semibold text-n-slate-12">
                {{ currentPlan.name }}
              </span>
            </div>
            <div v-if="currentSubscription" class="text-sm text-n-slate-11">
              <span v-if="currentSubscription.status === 'trialing'">
                {{
                  t('BILLING.STATUS.TRIAL_ENDS', {
                    days: currentSubscription.trial_days_remaining || 0,
                  })
                }}
              </span>
              <span v-else-if="currentSubscription.status === 'active'">
                {{
                  t('BILLING.STATUS.RENEWS_IN', {
                    days: currentSubscription.days_until_renewal,
                  })
                }}
              </span>
            </div>
          </div>
        </div>

        <!-- Plans Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <PlanCard
            v-for="plan in sortedPlans"
            :key="plan.id"
            :plan="plan"
            :billing-cycle="billingCycle"
            :is-current-plan="isCurrentPlan(plan)"
            :can-upgrade="canUpgradeTo(plan)"
            :can-downgrade="canDowngradeTo(plan)"
            :is-loading="isProcessing && selectedPlan?.id === plan.id"
            @select="handleSelectPlan"
            @upgrade="handleUpgrade"
            @downgrade="handleDowngrade"
          />
        </div>

        <!-- Help Text -->
        <div class="mt-8 text-center text-sm text-n-slate-10">
          <p>
            {{ t('BILLING.PRICING.HELP_TEXT') }}
          </p>
          <p class="mt-2">
            {{ t('BILLING.PRICING.CONTACT_SALES') }}
          </p>
        </div>
      </div>
    </template>
  </SettingsLayout>

  <!-- Confirm Dialog -->
  <Dialog
    ref="confirmDialogRef"
    :title="confirmDialogTitle"
    :confirm-button-label="t('BILLING.CONFIRM.PROCEED')"
    :cancel-button-label="t('BILLING.CONFIRM.CANCEL')"
    :is-loading="isProcessing"
    type="edit"
    @confirm="processAction"
    @close="closeConfirmDialog"
  >
    <div class="space-y-4">
      <p class="text-sm text-n-slate-11">
        {{ confirmDialogMessage }}
      </p>
      <!-- Gateway selector (only for subscribe action) -->
      <div v-if="confirmAction === 'subscribe'" class="space-y-2">
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('BILLING.GATEWAY.SELECT_PAYMENT') }}
        </p>
        <div class="flex gap-3">
          <button
            type="button"
            class="flex-1 p-3 rounded-xl border-2 text-sm font-medium transition-all text-left"
            :class="
              selectedGateway === 'stripe'
                ? 'border-n-blue-9 bg-n-blue-2 text-n-blue-11'
                : 'border-n-weak bg-n-solid-2 text-n-slate-11 hover:border-n-slate-7'
            "
            @click="selectedGateway = 'stripe'"
          >
            <span class="block font-semibold">
              {{ t('BILLING.GATEWAY.PAY_WITH_CARD') }}
            </span>
            <span class="block text-xs mt-0.5 opacity-75">
              {{ t('BILLING.GATEWAY.STRIPE_CHECKOUT_REDIRECT') }}
            </span>
          </button>
          <button
            type="button"
            class="flex-1 p-3 rounded-xl border-2 text-sm font-medium transition-all text-left"
            :class="
              selectedGateway === 'asaas'
                ? 'border-n-blue-9 bg-n-blue-2 text-n-blue-11'
                : 'border-n-weak bg-n-solid-2 text-n-slate-11 hover:border-n-slate-7'
            "
            @click="selectedGateway = 'asaas'"
          >
            <span class="block font-semibold">
              {{ t('BILLING.GATEWAY.PAY_WITH_PIX_BOLETO') }}
            </span>
            <span class="block text-xs mt-0.5 opacity-75">
              {{ t('BILLING.GATEWAY.ASAAS') }}
            </span>
          </button>
        </div>
      </div>
    </div>
  </Dialog>
</template>
